import React, { RefObject, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button, CloseIcon, CopyIcon, globalToastService, Tooltip, TOOLTIP_SURFACE } from '@repo/atomic-components';
import { tableToTsv } from '@/utils/tableToCsv';

export interface ChatTableDialogProps {
  visible: boolean;
  onHide: () => void;
  title?: string;
  tableChildren: React.ReactNode;
}

const COPY_TABLE_TOOLTIP = 'Copy table';

export const ChatTableDialog = ({ visible, onHide, title = 'Table', tableChildren }: ChatTableDialogProps) => {
  const titleId = useId();
  const tableRef = useRef<HTMLTableElement | null>(null);
  const copyButtonRef = useRef<HTMLSpanElement>(null);

  const handleCopyCsv = async () => {
    const table = tableRef.current;
    if (!table) return;
    const csvText = tableToTsv(table);
    await navigator.clipboard.writeText(csvText);
    globalToastService.show({
      closable: true,
      severity: 'success',
      summary: 'Copied to clipboard',
      life: 6000,
    });
  };

  useEffect(() => {
    if (!visible) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onHide();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visible, onHide]);

  if (!visible) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 p-[max(1rem,2vw)]"
      role="presentation"
      onClick={onHide}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="border-theme-border-grey bg-theme-white flex h-[min(82vh,53.75rem)] min-w-0 flex-col rounded-lg border shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-theme-border-grey flex shrink-0 items-center justify-between border-b px-6 py-4">
          <h2 id={titleId} className="text-theme-black truncate text-lg font-semibold">
            {title}
          </h2>
          <div className="flex items-center gap-2">
            <span ref={copyButtonRef} className="inline-flex">
              <Button
                type="button"
                outlined
                className="hover:bg-theme-background-grey border-theme-border-grey text-theme-black h-8 shrink-0 gap-2 px-4"
                icon={<CopyIcon />}
                label="Copy"
                aria-label={COPY_TABLE_TOOLTIP}
                onClick={handleCopyCsv}
              />
            </span>
            <Tooltip
              target={copyButtonRef as RefObject<HTMLElement>}
              content={COPY_TABLE_TOOLTIP}
              position="top"
              className={TOOLTIP_SURFACE}
            />
            <Button
              type="button"
              onClick={onHide}
              outlined
              aria-label="Close"
              className="hover:bg-theme-background-grey text-theme-black inline-flex h-[32px] w-[32px] shrink-0 cursor-pointer items-center justify-center rounded-md border-0 transition-colors"
            >
              <CloseIcon width={16} height={16} />
            </Button>
          </div>
        </div>

        <div className="min-h-0 min-w-0 flex-1 overflow-auto">
          <div className="p-4">
            <div className="rounded-lg border border-gray-200 bg-white">
              <table ref={tableRef} className="min-w-full divide-y divide-gray-200">
                {tableChildren}
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
